import type { GenerationJob } from './api-client.js';

/**
 * Pure view helpers for the generation lifecycle (R6/R11). Kept out of the
 * React Query hooks so the 2s poll cadence, the stop-on-terminal rule, and the
 * success→Assembly navigation decision are unit-testable without a render.
 */
// F-147 (US1, D2): each label honestly names the REAL pipeline state it maps to
// (the wire enum value is opaque/legacy and never user-visible). Kept in lock-step
// with the band module's PHI-free log copy so the subtitle and the activity log agree.
const STEP_LABELS: Record<GenerationJob['currentStep'], string> = {
  initiating_file_analysis: 'Starting generation…',
  extracting_data: 'Extracting data…',
  cross_referencing: 'Selecting test cases…',
  extracting_examples: 'Generating content…',
  creating_files: 'Finalising & verifying…',
};

export const isGenerationTerminal = (job: GenerationJob | undefined): boolean =>
  job?.status === 'succeeded' || job?.status === 'failed';

export const isGenerationSucceeded = (job: GenerationJob | undefined): boolean =>
  job?.status === 'succeeded';

/** 2s while in flight (SC-003), false once terminal so the poll stops (R6). */
export const generationPollInterval = (job: GenerationJob | undefined): number | false =>
  isGenerationTerminal(job) ? false : 2000;

export const stepLabel = (step: GenerationJob['currentStep']): string => STEP_LABELS[step];

export const currentStepLabel = (job: GenerationJob | undefined): string =>
  job ? stepLabel(job.currentStep) : 'Starting generation…';

// F-147 (US2, D4): within-stage easing constants. The displayed value eases out
// from the stage floor toward — but never reaching — an asymptote below the band
// ceiling, so the long content stage keeps visibly moving (SC-004) yet can never
// show a percentage ahead of the real stage / a premature 100% (FR-004/SC-005).
const EASE_TAU_MS = 45_000; // time constant — larger = slower, more linear creep
const BAND_CAP_FRACTION = 0.92; // asymptote at 92% of the band — strictly below ceil

/**
 * Ease the displayed progress within one stage's band. `floor` is the API's
 * ground-truth value (re-synced every poll); `ceil` is the stage's band ceiling
 * (from the shared domain table). The result:
 *   - starts at `floor` (elapsed 0),
 *   - eases out toward an asymptote strictly BELOW `ceil` (never crosses into the
 *     next band → never ahead of reality, FR-002/SC-005),
 *   - is clamped monotonic via `lastShown` (never moves backward, SC-003).
 * A pathological `lastShown` at/above the ceiling is clamped back below it, so the
 * indicator can never be pushed ahead of the real stage.
 */
export const easedProgress = (
  floor: number,
  ceil: number,
  stageElapsedMs: number,
  lastShown: number,
): number => {
  const span = Math.max(0, ceil - floor);
  const cap = floor + span * BAND_CAP_FRACTION; // strictly below ceil when span > 0
  const elapsed = Math.max(0, stageElapsedMs);
  const eased = cap - (cap - floor) * Math.exp(-elapsed / EASE_TAU_MS);
  // Monotonic within the band AND never past the cap: clamp lastShown into
  // [floor, cap] before taking the max, so the result is always in [floor, cap] ⊂ [floor, ceil).
  const flooredLast = Math.min(Math.max(lastShown, floor), cap);
  return Math.max(flooredLast, eased);
};
